import { useNavigate } from 'react-router-dom';
import { Button, Divider, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import useBooleanInput from '../../../hooks/useBooleanInput';
import { formatCurrency } from '../../../shared/utils/currencyUtils';
import PetDetailHeader from '../pet-detail-header';
import WellnessRenewalChangeConfirmationModal from '../wellness-renewal-change-confirmation-modal';
import { wellnessChangeSidebarStyle } from './wellness-change-sidebar-style';

function WellnessChangeSideBar({
  petData = {},
  wellnessFormattedData = {},
  onConfirmChanges = () => {},
  loading = false,
}) {
  const classes = wellnessChangeSidebarStyle();
  const navigate = useNavigate();

  const isChargedAnnually = !!wellnessFormattedData?.is_annualized;
  const months = isChargedAnnually ? 11 : 1;

  const transactionFee = 0;
  const subtotal = parseFloat(wellnessFormattedData.price) * months;
  const grandTotal = subtotal + transactionFee;

  const subTotalFormatted = formatCurrency(subtotal, {
    areCents: false,
  });

  const grandTotalFormatted = formatCurrency(grandTotal, {
    areCents: false,
  });

  const [
    isWellnessChangeConfirmationModalOpen,
    toggleWellnessChangeConfirmationModal,
  ] = useBooleanInput();

  return (
    <>
      <div className={classes.sidebarTitle}>
        <Typography className={classes.summary}>Summary</Typography>
      </div>

      <div className={classes.petPlanDetail}>
        <div className={classes.petPlanHeaderContainer}>
          <Typography className={classes.petPlanTitle}>{`${
            petData.name || ''
          }'s Plan`}</Typography>
          <Typography className={classes.petPlanTotal}>{`${subTotalFormatted} ${
            isChargedAnnually ? '/ yr' : '/ mo'
          }`}</Typography>
        </div>

        <PetDetailHeader petData={petData} />
      </div>

      <div className={classes.totalSummary}>
        <div className={classes.petPlanHeaderContainer}>
          <Typography className={classes.totalValues}>{`${
            isChargedAnnually ? 'Annual' : 'Monthly'
          } Subtotal:`}</Typography>
          <Typography className={classes.totalValues}>
            {subTotalFormatted}
          </Typography>
        </div>

        <div className={classes.petPlanHeaderContainer}>
          <Typography className={classes.totalValues}>
            {`${isChargedAnnually ? 'Annual' : 'Monthly'} Transaction Fee:`}
          </Typography>
          <Typography className={classes.totalValues}>
            {formatCurrency(transactionFee, { areCents: false })}
          </Typography>
        </div>

        <Divider className={classes.margin20v} />

        <div className={classes.petPlanHeaderContainer}>
          <Typography className={classes.finalTotalValues}>{`${
            isChargedAnnually ? 'Annual' : 'Monthly'
          } Grand Total:`}</Typography>
          <Typography className={classes.finalTotalValues}>
            {grandTotalFormatted}
          </Typography>
        </div>

        <Button
          fullWidth
          disabled={
            !wellnessFormattedData ||
            !!loading ||
            petData.wellness_plans
              .find(plan => plan.status === 'renew_pending')
              ?.tier.replace('.', '-') === wellnessFormattedData?.tier
          }
          onClick={() => {
            toggleWellnessChangeConfirmationModal();
          }}
          className={classes.margin10v}
          variant='contained'
          color='primary'
        >
          SAVE CHANGES
        </Button>

        <Button
          fullWidth
          disabled={!!loading}
          onClick={() => navigate(0)}
          className={classes.margin10v}
          variant='outlined'
          color='primary'
        >
          RESET & KEEP CURRENT POLICY
        </Button>
      </div>

      <WellnessRenewalChangeConfirmationModal
        onClose={toggleWellnessChangeConfirmationModal}
        isOpen={isWellnessChangeConfirmationModalOpen}
        loading={!!loading}
        onConfirmChanges={onConfirmChanges}
        petData={petData}
        renewalChangeData={wellnessFormattedData}
      />
    </>
  );
}

WellnessChangeSideBar.propTypes = {
  petData: PropTypes.shape({
    name: PropTypes.string,
    latest_pet_plan: PropTypes.shape({
      status: PropTypes.string,
      cost: PropTypes.number,
    }),
  }),
  wellnessFormattedData: PropTypes.object,
  loading: PropTypes.bool,
  onConfirmChanges: PropTypes.func,
};

export default WellnessChangeSideBar;
